import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { RoleRequirementsPanel } from "@/components/match/requirements/RoleRequirementsPanel";
import { hasListingRequirements } from "@/pages/share/listings/listing-requirements";
import { CompanyRequirementsPanel } from "@/components/match/requirements/CompanyRequirementsPanel";
import { hasCompanyRequirements } from "@/pages/share/listings/listing-search-target";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { DocumentTile } from "../ProjectDocuments";
import { Collapse } from "@/components/design/Collapse";
import { Button } from "@/components/design/Button";
import { ProfileSilhouette } from "@/components/design/ProfileSilhouette";
import { Switch } from "@/components/design/Switch";
import { AutoMatchSwitch } from "@/components/match/auto-match/AutoMatchSwitch";
import { dataToDesign } from "@/components/match/match-design-mapping";
import { EMPTY_MATCH_DATA } from "@/components/match/match-design-rules";
import { describesRole } from "@/components/match/match-mode";
import type { MatchData } from "@/components/match/match-design-types";
import {
  MatchSkillsDiagram,
  type MatchSkillEntry,
} from "@/components/match/requirements/MatchSkillsDiagram";
import { SubsectionHeader } from "@/components/match/requirements/row-primitives";
import { throwIfValidationRejected, useValidationFallback } from "@/components/match/use-match-editor";
import { userErrorMessage } from "@/lib/user-error";
import { projectWriteErrorMessage } from "@/lib/project-write-error";
import {
  AttachProjectDocumentDocument,
  BulkAddCandidatesDocument,
  BulkAddCompaniesToPoolDocument,
  DocumentContentDocument,
  CreateMatchDocument,
  DeleteMatchDocument,
  MatchMode,
  SetMatchAutoMatchDocument,
} from "@/graphql/generated/operations";
import type { KindToMatch, ToMatchMode, ToMatchTake } from "../dashboard-kinds";
import type { Msg } from "../project-mock-data";
import { hasMatchRequirements, type PlanPerson, type ProjectRequirements } from "../project-plan";
import { requirementsToMatchData } from "../requirements-to-match";

// Der Schritt zwischen Projekt und Match. Vorher führte hier ein blosser Link
// nach MATCH, und was davon mitging, erfuhr man erst drüben - nämlich nichts.
//
// Was hier steht, ist deshalb kein Formular, sondern eine Liste von Zusagen:
// Das nehme ich mit, das lasse ich hier. Abwählbar, weil ein Projekt mehrere
// Matches haben darf und das zweite selten dieselben Anforderungen braucht.
//
// GEZEIGT statt behauptet: die Fachkompetenz-Leiter und die Powers stehen hier so,
// wie sie im Anforderungen-Reiter stehen. Ein Wort wie "Gewichtung übernehmen"
// ist keine Zusage, die man prüfen kann - eine Silhouette, auf der ein Balken
// oben und einer unten steht, schon.
//
// Die Übernahme ist eine KOPIE, kein lebender Link. Zwei bearbeitbare Quellen
// für dieselbe Anforderung sind genau die Drift, gegen die die Projekt-Entität
// gebaut ist. Verbunden bleiben die beiden trotzdem: die Relation project_match
// hält fest, dass dieses Match zu diesem Projekt gehört, und im Projekt steht
// danach ein Verweis darauf.

// Welche Fragen und welche Zeilen eine Art anbietet und wie sie die Dinge nennt, steht in ihrer
// Konfiguration (`toMatch` in dashboard-kinds.tsx); dieser Schritt kennt keine Art beim Namen.

type Take = Record<ToMatchTake, boolean>;

/** Wer mitgehen kann. `note` steht unter dem Namen, etwa der Zustand einer Bewerbung. */
export type ToMatchPerson = PlanPerson & { note?: string };

// Dieselben Texte wie im Anlegen-Dialog (components/match/ModeSelect): wer den
// Modus einmal in MATCH gewählt hat, soll hier nicht dieselbe Entscheidung unter
// anderen Worten wiederfinden.
const MODES: Record<
  ToMatchMode,
  {
    title: { id: string; defaultMessage: string };
    slogan: { id: string; defaultMessage: string };
    output: { id: string; defaultMessage: string };
  }
> = {
  RANK_COMPANIES: {
    title: { id: "match.modePicker.rankCompaniesTitle", defaultMessage: "Unternehmen vergleichen" },
    slogan: { id: "match.modePicker.rankCompaniesSlogan", defaultMessage: "Du hast einen Bedarf und willst Unternehmen danach sortieren, wer am besten passt." },
    output: { id: "match.modePicker.rankCompaniesOutput", defaultMessage: "Rangliste" },
  },
  OPTIMIZE: {
    title: { id: "match.modePicker.optimizeTitle", defaultMessage: "Team optimieren" },
    slogan: {
      id: "match.modePicker.optimizeSlogan",
      defaultMessage:
        "Du hast einen Pool an Personen und willst das beste Team für ein Projekt oder eine Aufgabe zusammenstellen.",
    },
    output: { id: "match.modePicker.optimizeOutput", defaultMessage: "ein Team" },
  },
  ASSESS: {
    title: { id: "match.modePicker.assessTitle", defaultMessage: "Team bewerten" },
    slogan: {
      id: "match.modePicker.assessSlogan",
      defaultMessage:
        "Du hast bereits ein Team im Kopf und willst sehen, wie gut es passt und wo die Lücken sind.",
    },
    output: { id: "match.modePicker.assessOutput", defaultMessage: "Einschätzung + Lücken" },
  },
  RANK: {
    title: { id: "match.modePicker.rankTitle", defaultMessage: "Kandidaten vergleichen" },
    slogan: {
      id: "match.modePicker.rankSlogan",
      defaultMessage:
        "Du besetzt eine offene Stelle und willst Kandidaten danach sortieren, wer am besten passt.",
    },
    output: { id: "match.modePicker.rankOutput", defaultMessage: "Rangliste" },
  },
};

// Vom Wort-Token auf den GraphQL-Enum. Absichtlich eine vollständige Tabelle und
// kein Cast: kommt ein vierter Modus dazu, bricht hier der Build, statt dass ein
// unbekannter String bis zur Mutation durchrutscht.
const MODE_ENUM: Record<ToMatchMode, MatchMode> = {
  OPTIMIZE: MatchMode.Optimize,
  ASSESS: MatchMode.Assess,
  RANK: MatchMode.Rank,
  RANK_COMPANIES: MatchMode.RankCompanies,
};

/** Was nach dem Verknüpfen nicht gelang; mehr als eines heißt `several`. */
type Missing = "people" | "documents" | "autoMatch" | "several";

const NO_DOCUMENTS: readonly { id: string; name: string; size?: number }[] = [];
const NO_PEOPLE: readonly ToMatchPerson[] = [];

export function ProjectToMatchScreen({
  toMatch,
  onStart,
  link,
  title,
  description,
  requirements,
  people: givenPeople,
  peopleLoading = false,
  peopleError,
  documents = NO_DOCUMENTS,
  companyRequirements,
  requirementsData,
  companies = NO_PEOPLE,
  withheld,
  actingAsCompanyId = null,
}: {
  /** Was die Art hier anbietet und wie sie es nennt. */
  toMatch: KindToMatch;
  onStart?: () => void;
  /**
   * Warum aus dem Objekt niemand mitgehen kann, obwohl die Art es anbietet, etwa ohne Leserecht auf Bewerbungen.
   * Steht unter der Fragen-Auswahl statt des Hinweises auf übernommene Unternehmen: kein Zugriff ist nicht "keine".
   */
  withheld?: Msg;
  companyRequirements?: MatchData;
  requirementsData?: MatchData;
  companies?: readonly { id: string; name: string }[];
  /**
   * Verknüpft das neue Match mit dem Objekt, aus dem es entsteht (Projekt oder Ausschreibung; die
   * Quelle des Dashboards weiß, über welche Mutation). Fehlt, solange es nichts zu verknüpfen gibt.
   */
  link: ((matchId: string) => Promise<void>) | null;
  /** Context for project write errors; listing links carry their own policy. */
  actingAsCompanyId?: string | null;
  title: string;
  description: string;
  requirements: ProjectRequirements;
  /** Wer mitgehen kann; beim Projekt Mitglieder mit Zugriff und Gefragte, in dieser Reihenfolge. */
  people: readonly ToMatchPerson[];
  /** Die Liste ist noch nicht vollständig; angelegt wird erst danach, sonst fehlte still, wer nachkommt. */
  peopleLoading?: boolean;
  /** Die Liste ließ sich nicht laden; die Zeile sagt es, angelegt werden kann trotzdem. */
  peopleError?: unknown;
  /** Die Dokumente des Projekts, als Verweise auf die eigene Bibliothek. */
  documents?: readonly { id: string; name: string; size?: number }[];
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const validationFallback = useValidationFallback();
  const [createMatch] = useMutation(CreateMatchDocument);
  const [creating, setCreating] = useState(false);
  const inFlight = useRef(false);
  const [addCompanies] = useMutation(BulkAddCompaniesToPoolDocument);
  const [addCandidates] = useMutation(BulkAddCandidatesDocument);
  const [attachDocument] = useMutation(AttachProjectDocumentDocument);
  const [deleteMatch] = useMutation(DeleteMatchDocument);
  const [setMatchAutoMatch] = useMutation(SetMatchAutoMatchDocument);
  const apollo = useApolloClient();
  const [error, setError] = useState<string | null>(null);
  /** Ein angelegtes und verknüpftes Match, in das nicht alles übernommen werden konnte. */
  const [partial, setPartial] = useState<{ matchId: string; missing: Missing } | null>(null);
  const [take, setTake] = useState<Take>({
    description: true,
    requirements: true,
    scorecard: true,
    people: true,
    documents: true,
  });
  /** Gewählt und von der Art überhaupt angeboten. */
  const wants = (key: ToMatchTake) => (take[key] || toMatch.requiredTakes?.includes(key)) && toMatch.takes.includes(key);
  // Abgewählt statt ausgewählt: die Namen der Gefragten lädt `useProjectPeople`
  // nach, die Liste ist beim ersten Render also noch nicht vollständig. Wer
  // später dazukommt, ist so trotzdem dabei, ohne dass ein Effekt den Zustand
  // nachträglich korrigieren muss.
  const [dropped, setDropped] = useState<readonly string[]>([]);
  const [droppedDocuments, setDroppedDocuments] = useState<readonly string[]>([]);
  const [mode, setMode] = useState<ToMatchMode>(toMatch.modes[0]);
  // Eine einzige Frage ist keine Wahl. Sie steht als dieselbe Karte da, ausgewählt und nicht schaltbar,
  // wie ein Modus, der nicht mehr wechseln kann (ModeSelect in den Match-Einstellungen).
  const fixedMode = toMatch.modes.length === 1;
  // Standardmäßig an: wer aus einer Ausschreibung matcht, will die Bewerbungen bewertet sehen, die
  // danach noch kommen. Abschalten ist eine bewusste Entscheidung gegen die Kosten.
  const [autoMatch, setAutoMatch] = useState(true);
  const offersAutoMatch = toMatch.autoMatch && (mode === "RANK" || mode === "RANK_COMPANIES");

  // Eine Liste, die sich nicht vollständig laden ließ, bietet niemanden an, auch nicht den Teil, der
  // schon da ist: sonst ginge still nur ein Ausschnitt mit.
  const people = peopleError ? NO_PEOPLE : givenPeople;
  const takenPeople = people.filter((p) => !dropped.includes(p.id));
  const takenDocuments = documents.filter((d) => !droppedDocuments.includes(d.id));
  const missingRequirements = !!toMatch.requiredTakes?.includes("requirements") && !(mode === "RANK_COMPANIES" ? companyRequirements && hasCompanyRequirements(companyRequirements) : requirementsData ? hasListingRequirements(requirementsData) : hasMatchRequirements(requirements));
  const requirementCount = mode === "RANK_COMPANIES"
    ? companyRequirements?.companyConditions?.filter(row => row.active).length ?? 0
    : requirementsData ? requirementsData.conditions.filter(row => row.active).length : requirements.expertise.length;
  const elementWeights = requirementsData?.teamProfile?.elementWeights ?? requirements.elementWeights;
  const hasWeights = Object.values(elementWeights).some(weight => Number.isFinite(weight) && weight > 0);
  // Rank besetzt EINE Stelle, dort ist "mindestens drei Personen können das"
  // bedeutungslos. Die Zahl verschwindet deshalb sichtbar aus der Vorschau,
  // sobald der Modus gewechselt wird - und genau so wird sie auch geschrieben.
  const carriesPersonCount = !describesRole(mode);

  const entries: MatchSkillEntry[] = requirements.expertise.map((r) => ({
    id: r.id,
    description: r.description,
    level: r.level,
    requirement: r.requirement,
    ...(carriesPersonCount && r.personCount !== undefined ? { personCount: r.personCount } : {}),
  }));

  async function go() {
    if (inFlight.current) return;
    inFlight.current = true;
    setCreating(true);
    try {
      onStart?.();
      await createAndFill();
    } finally {
      inFlight.current = false;
      setCreating(false);
    }
  }

  async function createAndFill() {
    if (missingRequirements) {
      setError(intl.formatMessage({ id: "share.listings.match.needsRequirements" }));
      return;
    }
    setError(null);
    let matchId: string;
    try {
      const inherited = requirementsToMatchData(requirements, mode);

      const data: MatchData = {
        ...EMPTY_MATCH_DATA,
        aiEnabled: toMatch.aiEnabledByDefault ?? EMPTY_MATCH_DATA.aiEnabled,
        ...(requirementsData ?? {}),
        matchTitle: title,
        ...(wants("description") ? { projectName: title, projectDescription: description } : {}),
        teamProfile: wants("scorecard") ? requirementsData?.teamProfile ?? inherited.teamProfile : EMPTY_MATCH_DATA.teamProfile,
        conditions: mode === "RANK_COMPANIES" ? [] : wants("requirements") ? requirementsData?.conditions ?? inherited.conditions : [],
        ...(mode === "RANK_COMPANIES" && wants("requirements") ? {
          companyConditions: companyRequirements?.companyConditions ?? [], unknownCompanyConditions: companyRequirements?.unknownCompanyConditions,
        } : {}),
      };

      const result = await createMatch({
        variables: {
          input: {
            data: {
              matchMode: MODE_ENUM[mode],
              name: title || null,
              description: wants("description") ? description || null : null,
              design: dataToDesign(data),
            },
          },
        },
      });
      const payload = result.data?.createMatch;
      throwIfValidationRejected(payload, "ValidateMatchResult", validationFallback);
      if (payload?.__typename !== "CreateMatchResult") return;
      matchId = payload.result.id;
    } catch (e) {
      setError(userErrorMessage(intl, e));
      return;
    }

    // Verknüpfen direkt nach dem Anlegen, vor dem Befüllen: scheitert danach etwas, ist das Match
    // unvollständig, aber nie eine Waise, von der das Projekt nichts weiß (spark#1001). Scheitert
    // das Verknüpfen selbst, ist es noch löschbar, und kein Match ist besser als ein verwaistes.
    if (link) {
      try {
        await link(matchId);
      } catch (e) {
        const removed = await deleteMatch({ variables: { input: { id: matchId } } }).then(
          () => true,
          () => false,
        );
        setError(
          removed
            ? projectWriteErrorMessage(intl, e, actingAsCompanyId)
            : intl.formatMessage(toMatch.orphaned),
        );
        return;
      }
    }

    // Befüllen: Schalter, Personen und Dokumente sind unabhängig voneinander, ein Fehlschlag beim
    // einen hält das andere nicht auf. Was fehlt, wird genannt und lässt sich im Match nachholen.
    const failed: Exclude<Missing, "several">[] = [];

    // Der Schalter vor den Personen: die Liste der Bewerbenden ist schon geladen, und wer sich
    // zwischen Übernahme und Einschalten bewürbe, stünde sonst weder bei den Kandidaten noch würde er
    // bewertet. Eingeschaltet zuerst, löst jede Bewerbung ab jetzt einen Lauf aus.
    if (offersAutoMatch && autoMatch) {
      try {
        await setMatchAutoMatch({ variables: { input: { matchId, enabled: true } } });
      } catch {
        failed.push("autoMatch");
      }
    }

    // Die Menschen sind das einzige hier, was keine Kopie im Dokument ist:
    // ein Kandidat ist eine eigene Zeile (match_candidate) und braucht das
    // Match, also geht er erst nach dem Anlegen hin. Dieselbe Mutation, die
    // auch der Match-Editor für "Personen hinzufügen" benutzt.
    if (!(offersAutoMatch && autoMatch && !failed.includes("autoMatch")) && wants("people") && takenPeople.length > 0) {
      try {
        await addCandidates({
          variables: {
            input: {
              match: { id: matchId },
              data: takenPeople.map((person) => ({ person: { id: person.id }, locked: false })),
            },
          },
        });
      } catch {
        failed.push("people");
      }
    }

    if (mode === "RANK_COMPANIES" && companies.length && !(offersAutoMatch && autoMatch && !failed.includes("autoMatch"))) {
      try { await addCompanies({ variables: { input: { match: { id: matchId }, data: companies.map((company) => ({ company: { id: company.id } })) } } }); }
      catch { failed.push("people"); }
    }

    // Dokumente gehen als KOPIE mit, nicht als Verweis: das Match führt seine
    // Dateien in `design.project.documents` mit eigener backdrop-Id, und
    // `attachProjectDocument` nimmt nur rohen Inhalt entgegen. Also wird je
    // Dokument der Inhalt geholt und angehängt. Ein Verweis wäre billiger,
    // bräuchte aber ein Feld in genesis, das es nicht gibt - und er würde der
    // Zusage widersprechen, dass Änderungen im Match das Projekt nicht
    // berühren.
    if (wants("documents") && takenDocuments.length > 0) {
      try {
        const files = (
          await Promise.all(
            takenDocuments.map(async (doc) => {
              const { data } = await apollo.query({
                query: DocumentContentDocument,
                variables: { id: doc.id },
                fetchPolicy: "network-only",
              });
              const file = data?.document?.content;
              if (!file?.content) return null;
              return {
                fileName: data?.document?.fileName?.trim() || doc.name,
                mimeType: file.mimeType ?? "application/octet-stream",
                content: file.content,
              };
            }),
          )
        ).flatMap((f) => (f ? [f] : []));
        if (files.length > 0) {
          await attachDocument({ variables: { input: { matchId, files } } });
        }
      } catch {
        failed.push("documents");
      }
    }

    if (failed.length > 0) {
      setPartial({ matchId, missing: failed.length === 1 ? failed[0]! : "several" });
      return;
    }
    navigate(`/match/${matchId}${offersAutoMatch && autoMatch ? "?step=result" : ""}`);
  }

  const peopleDetail = peopleLoading
    ? intl.formatMessage({ id: "common.loading", defaultMessage: "Laden…" })
    : peopleError
      ? userErrorMessage(intl, peopleError)
      : intl.formatMessage(toMatch.people.detail, { count: takenPeople.length, total: people.length });

  const rowsByKey: Record<ToMatchTake, { label: string; detail: string; disabled?: boolean }> = {
    description: {
      label: intl.formatMessage({ id: "project.toMatch.description", defaultMessage: "Beschreibung" }),
      detail: description
        ? description.slice(0, 90)
        : intl.formatMessage({ id: "project.toMatch.nothingYet", defaultMessage: "Noch nichts hinterlegt" }),
      disabled: !description,
    },
    requirements: {
      label: intl.formatMessage({ id: "project.toMatch.requirements", defaultMessage: "Anforderungen" }),
      detail: intl.formatMessage(
        {
          id: requirementsData || mode === "RANK_COMPANIES" ? "project.toMatch.criteriaDetail" : "project.toMatch.requirementsDetail",
          defaultMessage:
            "{count, plural, =0 {Noch nichts hinterlegt} one {Eine Fachkompetenz} other {# Fachkompetenzen}}",
        },
        { count: requirementCount },
      ),
      disabled: requirementCount === 0,
    },
    scorecard: {
      label: intl.formatMessage({ id: "project.toMatch.scorecard", defaultMessage: "Power-Scorecard" }),
      detail: hasWeights
        ? intl.formatMessage({ id: "project.toMatch.scorecardSet", defaultMessage: "Gewichtung übernehmen" })
        : intl.formatMessage({ id: "project.toMatch.nothingYet", defaultMessage: "Noch nichts hinterlegt" }),
      disabled: !hasWeights,
    },
    documents: {
      label: intl.formatMessage({ id: "project.toMatch.documents", defaultMessage: "Dokumente" }),
      detail: intl.formatMessage(
        {
          id: "project.toMatch.documentsDetail",
          defaultMessage:
            "{count, plural, =0 {Noch nichts hinterlegt} one {Ein Dokument} other {# Dokumente}}",
        },
        { count: takenDocuments.length },
      ),
      disabled: documents.length === 0,
    },
    people: {
      label: intl.formatMessage(toMatch.people.label),
      detail: peopleDetail,
      disabled: people.length === 0,
    },
  };
  const rows = toMatch.takes.map((key) => ({ key, ...rowsByKey[key] }));

  // Abgewählt heisst weg, nicht blass. Eine gedimmte Fachkompetenz-Leiter blieb
  // eine Fachkompetenz-Leiter: sie nahm denselben Platz ein, und die Liste sah
  // gleich lang aus, egal wie viel man abgewählt hatte. Eingeklappt wird die
  // Entscheidung sofort sichtbar, und was übrig bleibt, ist genau das, was
  // mitgeht. Der Schalter daneben sagt weiterhin, dass es die Zeile noch gibt.
  function preview(active: boolean, children: ReactNode) {
    return (
      <Collapse open={active}>
        <div className="mt-3">{children}</div>
      </Collapse>
    );
  }

  return (
    <div className="max-w-3xl">
      <p className="smycles-field-meta max-w-[62ch] text-ink">
        {intl.formatMessage(toMatch.sub)}
      </p>

      <div className="mt-5">
        <SubsectionHeader
          title={intl.formatMessage({
            id: "match.modePicker.title",
            defaultMessage: "Welche Frage soll dieses Match beantworten?",
          })}
        />
        <div
          className={`mt-1 grid gap-3 ${fixedMode ? "" : "@sm:grid-cols-3"}`}
          role="radiogroup"
          aria-label={intl.formatMessage({ id: "match.modePicker.title" })}
        >
          {toMatch.modes.map((option) => {
            const m = MODES[option];
            const selected = option === mode;
            return (
              <button
                key={option}
                id={`project-to-match-mode-${option.toLowerCase()}`}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={fixedMode}
                onClick={() => setMode(option)}
                className={`box-flat bg-page flex flex-col gap-2 p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui disabled:cursor-default ${
                  selected ? "smycles-selected ring-2 ring-accent-decoration" : "hover:ring-1 hover:ring-ink-ui"
                }`}
              >
                <span className="font-serif text-smycles-title leading-none">
                  <FormattedMessage {...m.title} />
                </span>
                <span className="text-smycles-meta leading-snug text-ink">
                  <FormattedMessage {...m.slogan} />
                </span>
                <span className="smycles-field-label mt-auto pt-1">
                  <FormattedMessage id="match.modePicker.outputLabel" defaultMessage="Ergebnis" />
                  {": "}
                  <FormattedMessage {...m.output} />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {withheld ? <p className="smycles-field-meta mt-4 max-w-[62ch] text-ink-muted">{intl.formatMessage(withheld)}</p>
        : mode === "RANK_COMPANIES" ? <p className="smycles-field-meta mt-4 text-ink-muted"><FormattedMessage id="share.listings.companyMatch.importHint" values={{ count: companies.length }} /></p> : null}
      {offersAutoMatch ? (
        <div className="mt-6 box bg-surface p-4">
          <AutoMatchSwitch
            checked={autoMatch}
            onChange={setAutoMatch}
            disabled={creating}
            hint={intl.formatMessage({
              id: "match.autoMatch.createHint",
              defaultMessage:
                "Bewertet vorhandene und neue Bewerbungen und speichert die Rangliste automatisch.",
            })}
          />
        </div>
      ) : null}

      <div className="mt-6">
        <SubsectionHeader
          title={intl.formatMessage({
            id: "project.toMatch.takeTitle",
            defaultMessage: "Was mitgeht",
          })}
        />
        <div className="mt-1 box bg-surface p-4">
          <ul className="divide-y divide-ink/10">
            {rows.map((row) => {
              const automaticPeople = row.key === "people" && offersAutoMatch && autoMatch;
              const required = toMatch.requiredTakes?.includes(row.key) ?? false;
              const active = (automaticPeople || required || take[row.key]) && !row.disabled;
              return (
                <li key={row.key} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className={`smycles-field-value ${row.disabled ? "text-ink-muted" : ""}`}>{row.label}</div>
                      <div className="smycles-field-meta mt-0.5 truncate text-ink-muted">{row.detail}</div>
                    </div>
                    <Switch
                      checked={active}
                      disabled={row.disabled || automaticPeople || required}
                      onChange={(next) => setTake((cur) => ({ ...cur, [row.key]: next }))}
                      ariaLabel={row.label}
                    />
                  </div>

                  {/* Die Leiter ist dieselbe wie im Anforderungen-Reiter, nur
                      unbedienbar: Stufen, Muss-Schloss und Personenzahl liest man
                      hier genauso ab, geändert wird drüben. */}
                  {row.key === "requirements" && mode !== "RANK_COMPANIES" && requirementCount > 0
                    ? preview(
                        active,
                        requirementsData ? <RoleRequirementsPanel data={requirementsData} readOnly update={() => undefined} /> : <MatchSkillsDiagram
                          entries={entries}
                          readOnly
                          personCount={carriesPersonCount}
                          onToggleRequirement={() => {}}
                          onAdd={() => {}}
                          onRename={() => {}}
                          onDelete={() => {}}
                        />,
                      )
                    : null}

                  {row.key === "requirements" && mode === "RANK_COMPANIES" && companyRequirements
                    ? preview(active, <CompanyRequirementsPanel data={companyRequirements} readOnly update={() => undefined} />) : null}
                  {/* Die Silhouette statt des Editors: hier wird nichts gewichtet,
                      hier wird gezeigt, wie gewichtet wurde. Ohne Klick-Handler
                      rendert der Baustein als reine Anzeige. */}
                  {row.key === "scorecard" && hasWeights
                    ? preview(
                        active,
                        <ProfileSilhouette elementWeights={elementWeights} className="px-1" />,
                      )
                    : null}

                  {/* Dieselben Kacheln wie im Dokumente-Reiter, dasselbe Raster:
                      ein Dokument sieht überall gleich aus, und das ✕ nimmt es
                      aus der Übernahme - nicht aus dem Projekt. */}
                  {row.key === "documents" && documents.length > 0
                    ? preview(
                        active,
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,9rem))] gap-3">
                          {documents.map((doc) => (
                            <DocumentTile
                              key={doc.id}
                              id={`project-to-match-document-${doc.id}`}
                              document={doc}
                              dropped={droppedDocuments.includes(doc.id)}
                              onRemove={() =>
                                setDroppedDocuments((cur) =>
                                  cur.includes(doc.id) ? cur.filter((id) => id !== doc.id) : [...cur, doc.id],
                                )
                              }
                            />
                          ))}
                        </div>,
                      )
                    : null}

                  {/* Einzeln abwählbar, nicht nur als Ganzes: das zweite Match
                      eines Projekts sucht selten für dieselben Leute, und wer
                      hier alle mitnimmt, muss sie drüben wieder aussortieren.
                      Dieselbe Zeile wie in der Community-Auswahl und im
                      Team-Reiter, damit derselbe Mensch überall gleich aussieht. */}
                  {row.key === "people" && people.length > 0
                    ? preview(
                        active,
                        <div className="space-y-2">
                          {people.map((person) => {
                            const taken = automaticPeople || !dropped.includes(person.id);
                            return (
                              <PersonResultRow
                                key={person.id}
                                person={person}
                                title={person.name}
                                sub={person.note ?? ""}
                                embedded
                                actions={
                                  <Button
                                    id={`project-to-match-person-${person.id}`}
                                    variant={taken ? "primary" : "secondary"}
                                    size="sm"
                                    disabled={!active || automaticPeople}
                                    onClick={() =>
                                      setDropped((cur) =>
                                        taken ? [...cur, person.id] : cur.filter((id) => id !== person.id),
                                      )
                                    }
                                  >
                                    {taken ? (
                                      <FormattedMessage id="project.toMatch.peopleTaken" defaultMessage="Geht mit" />
                                    ) : (
                                      <FormattedMessage
                                        id="project.toMatch.peopleDropped"
                                        defaultMessage="Bleibt hier"
                                      />
                                    )}
                                  </Button>
                                }
                              />
                            );
                          })}
                        </div>,
                      )
                    : null}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <p className="smycles-field-meta mt-3 max-w-[62ch] text-ink-muted">
        {intl.formatMessage(toMatch.copyNote)}
      </p>

      {error ? <p id="project-to-match-error" className="mt-3 smycles-field-meta text-ink">{error}</p> : null}

      {partial ? (
        <div className="mt-5">
          <p id="project-to-match-partial" role="alert" className="smycles-field-meta text-ink">
            {intl.formatMessage(toMatch.partial, { missing: partial.missing })}
          </p>
          <Button id="project-to-match-openpartial" className="mt-3" variant="primary" onClick={() => navigate(`/match/${partial.matchId}`)}>
            <FormattedMessage id="project.toMatch.openPartial" defaultMessage="Match öffnen" />
          </Button>
        </div>
      ) : (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <Button id="project-to-match-go" variant="primary" onClick={go} disabled={creating || !link || peopleLoading || missingRequirements}>
            {creating ? (
              <FormattedMessage id="project.toMatch.creating" defaultMessage="Match wird angelegt …" />
            ) : (
              <FormattedMessage id="project.toMatch.go" defaultMessage="Match anlegen und öffnen" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
